import { Select, SelectItem } from '@heroui/react';
import type { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useFetchAccountCategoriesQuery } from '@/slices/account-api-slice.ts';
import type { FormControlProps } from '../../types/form-props.type.ts';

interface AccountCategorySelectorProps extends FormControlProps {
  accountId?: number;
  description?: string;
}

const DEFAULT_CATEGORY_DESCRIPTION =
  'Rep categories set how often this person can book meetings with this office. Choose the one that matches their visit cadence here.';

const AccountCategorySelector: FC<AccountCategorySelectorProps> = ({
  label = 'Rep category',
  name,
  displayProperty = 'description',
  accountId,
  description = DEFAULT_CATEGORY_DESCRIPTION,
  disabled,
  required,
  validatorFn,
}) => {
  const { control } = useFormContext();
  const { categories, isLoading } = useFetchAccountCategoriesQuery(null, {
    selectFromResult: ({ data, isLoading }) => ({
      categories: (accountId ? data?.filter(x => x.accountId === accountId) : data) ?? [],
      isLoading,
    }),
  });
  const hasNoCategories = !categories.length;

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: required ? 'This field is required' : undefined,
        validate: validatorFn,
      }}
      render={({ field, fieldState }) => (
        <Select
          label={label}
          placeholder={hasNoCategories ? 'No categories available' : `Select ${label}`}
          description={description}
          variant='bordered'
          isRequired={required}
          isDisabled={disabled || isLoading || hasNoCategories}
          isInvalid={fieldState.invalid}
          errorMessage={fieldState.error?.message}
          selectedKeys={field.value?.id ? new Set([field.value.id.toString()]) : new Set([])}
          items={categories}
          onSelectionChange={keys => {
            const selectedId = Array.from(keys)[0];
            if (!selectedId) {
              field.onChange(null);
              return;
            }

            field.onChange(categories.find(x => x.id === parseInt(selectedId as string, 10)));
          }}
        >
          {option => (
            <SelectItem
              key={option.id.toString()}
              textValue={`${option[displayProperty]} (${option.frequency.description})`}
            >
              {option[displayProperty]} ({option.frequency.description})
            </SelectItem>
          )}
        </Select>
      )}
    />
  );
};

export default AccountCategorySelector;
